import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Copy, AlertTriangle, Check, ChevronsUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProductSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  produtos: Produto[];
  disabled?: boolean;
}

function ProductSelector({ value, onValueChange, produtos = [], disabled }: ProductSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Add debugging
  console.log("ProductSelector render:", { value, produtos: produtos?.length, disabled });

  const selectedProduct = produtos.find(p => p.id === value);

  const filteredProducts = (produtos || []).filter(produto => {
    if (!produto || !produto.nome_produto) return false;
    
    const searchTerm = searchValue.toLowerCase();
    const productName = produto.nome_produto.toLowerCase();
    const productId = produto.id.toLowerCase();
    
    // Check product name and ID
    if (productName.includes(searchTerm) || productId.includes(searchTerm)) {
      return true;
    }
    
    // Check categories safely
    if (produto.categorias_n3) {
      if (produto.categorias_n3.nome?.toLowerCase().includes(searchTerm)) return true;
      if (produto.categorias_n3.categorias_n2?.nome?.toLowerCase().includes(searchTerm)) return true;
      if (produto.categorias_n3.categorias_n2?.categorias_n1?.nome?.toLowerCase().includes(searchTerm)) return true;
    }
    
    return false;
  }) || [];

  console.log("Filtered products:", filteredProducts?.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedProduct ? selectedProduct.nome_produto : "Selecione um produto..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="flex flex-col">
          <div className="p-2">
            <Input 
              placeholder="Buscar produto..." 
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full"
            />
          </div>
          <ScrollArea className="max-h-64">
            <div className="p-1">
              {filteredProducts.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Nenhum produto encontrado.
                </div>
              ) : (
                filteredProducts.map((produto) => (
                  <div
                    key={produto.id}
                    className={cn(
                      "flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-accent hover:text-accent-foreground",
                      value === produto.id && "bg-accent text-accent-foreground"
                    )}
                    onClick={() => {
                      onValueChange(produto.id === value ? "" : produto.id);
                      setOpen(false);
                      setSearchValue("");
                    }}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        value === produto.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">{produto.nome_produto}</span>
                      {produto.categorias_n3 && (
                        <span className="text-xs text-muted-foreground truncate">
                          {produto.categorias_n3?.categorias_n2?.categorias_n1?.nome} &gt; {produto.categorias_n3?.categorias_n2?.nome} &gt; {produto.categorias_n3?.nome}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface OrcamentoItem {
  id?: string;
  id_produto: string;
  quantidade: number;
  custo_unitario: number;
  valor_unitario: number;
  fee_percent: number;
  observacao: string;
  produto?: {
    nome_produto: string;
    custo_produto?: number;
    minimo_custo?: number;
    maximo_custo?: number;
    minimo_fee?: number;
    maximo_fee?: number;
    fee?: number;
    categorias_n3?: {
      nome: string;
      categorias_n2?: {
        nome: string;
        categorias_n1?: {
          nome: string;
        };
      };
    };
  };
}

interface Produto {
  id: string;
  nome_produto: string;
  custo_produto?: number;
  minimo_custo?: number;
  maximo_custo?: number;
  minimo_fee?: number;
  maximo_fee?: number;
  fee?: number;
  categorias_n3?: {
    nome: string;
    categorias_n2?: {
      nome: string;
      categorias_n1?: {
        nome: string;
      };
    };
  };
}

interface OrcamentoProdutosTabProps {
  orcamentoId: string;
  projetoId?: string;
  readonly?: boolean;
}

export function OrcamentoProdutosTab({ orcamentoId, projetoId, readonly = false }: OrcamentoProdutosTabProps) {
  const [items, setItems] = useState<OrcamentoItem[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [valorMaximoProjeto, setValorMaximoProjeto] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const loadProdutos = async () => {
    try {
      const { data, error } = await supabase
        .from("produtos_insumos")
        .select(`
          id,
          nome_produto,
          custo_produto,
          minimo_custo,
          maximo_custo,
          minimo_fee,
          maximo_fee,
          fee,
          categorias_n3(
            nome,
            categorias_n2(
              nome,
              categorias_n1(nome)
            )
          )
        `);

      if (error) throw error;
      setProdutos(data || []);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar produtos",
        variant: "destructive",
      });
    }
  };

  const loadValorMaximoProjeto = async () => {
    if (!projetoId) return;

    try {
      const { data, error } = await supabase
        .from("projetos")
        .select("valor_maximo")
        .eq("id", projetoId)
        .single();

      if (error) throw error;
      setValorMaximoProjeto(data?.valor_maximo || null);
    } catch (error) {
      console.error("Erro ao carregar valor máximo do projeto:", error);
    }
  };

  const loadItems = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("orcamento_itens")
        .select(`
          id,
          id_produto,
          quantidade,
          custo_unitario,
          valor_unitario,
          fee_percent,
          observacao,
          produtos_insumos(
            nome_produto,
            custo_produto,
            minimo_custo,
            maximo_custo,
            minimo_fee,
            maximo_fee,
            fee,
            categorias_n3(
              nome,
              categorias_n2(
                nome,
                categorias_n1(nome)
              )
            )
          )
        `)
        .eq("id_orcamento", orcamentoId);

      if (error) throw error;

      const mappedItems = (data || []).map((item: any) => ({
        id: item.id,
        id_produto: item.id_produto,
        quantidade: item.quantidade,
        custo_unitario: item.custo_unitario,
        valor_unitario: item.valor_unitario,
        fee_percent: item.fee_percent || 0,
        observacao: item.observacao || "",
        produto: item.produtos_insumos,
      }));

      setItems(mappedItems);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar itens do orçamento",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProdutos();
    loadValorMaximoProjeto();
    loadItems();
  }, [orcamentoId, projetoId]);

  const addNewItem = () => {
    const newItem: OrcamentoItem = {
      id_produto: "",
      quantidade: 1,
      custo_unitario: 0,
      valor_unitario: 0,
      fee_percent: 0,
      observacao: "",
    };
    setItems([...items, newItem]);
  };

  const removeItem = async (index: number) => {
    const item = items[index];
    
    if (item.id) {
      try {
        const { error } = await supabase
          .from("orcamento_itens")
          .delete()
          .eq("id", item.id);

        if (error) throw error;
        
        toast({
          title: "Sucesso",
          description: "Item removido com sucesso",
        });
      } catch (error) {
        toast({
          title: "Erro",
          description: "Erro ao remover item",
          variant: "destructive",
        });
        return;
      }
    }

    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const duplicateItem = (index: number) => {
    const item = items[index];
    const duplicatedItem: OrcamentoItem = {
      ...item,
      id: undefined, // Remove ID para criar novo item
    };
    const newItems = [...items];
    newItems.splice(index + 1, 0, duplicatedItem);
    setItems(newItems);
  };

  const updateItem = (index: number, field: keyof OrcamentoItem, value: any) => {
    const newItems = [...items];
    const item = newItems[index];
    
    if (field === "id_produto") {
      const produto = produtos.find(p => p.id === value);
      console.log("Selected product:", produto);
      
      if (produto) {
        item.id_produto = value;
        item.produto = produto;
        item.quantidade = 1;
        
        // Preencher custo unitário
        item.custo_unitario = produto.custo_produto || 0;
        console.log("Setting custo_unitario:", produto.custo_produto);
        
        // Preencher fee percent
        item.fee_percent = produto.fee || 0;
        console.log("Setting fee_percent:", produto.fee);
        
        // Calcular valor unitário baseado no fee
        if (item.fee_percent > 0) {
          item.valor_unitario = item.custo_unitario * (1 + (item.fee_percent / 100));
        } else {
          item.valor_unitario = item.custo_unitario;
        }
        console.log("Calculated valor_unitario:", item.valor_unitario);
      }
    } else if (field === "fee_percent") {
      item.fee_percent = value;
      // Recalcular valor unitário
      item.valor_unitario = item.custo_unitario * (1 + (value / 100));
    } else if (field === "custo_unitario") {
      item.custo_unitario = value;
      // Se há fee definido, recalcular valor unitário
      if (item.fee_percent > 0) {
        item.valor_unitario = value * (1 + (item.fee_percent / 100));
      }
    } else if (field === "valor_unitario") {
      item.valor_unitario = value;
      // Recalcular fee percent
      if (item.custo_unitario > 0) {
        item.fee_percent = ((value - item.custo_unitario) / item.custo_unitario) * 100;
      }
    } else {
      (item as any)[field] = value;
    }
    
    setItems(newItems);
  };

  const validateItem = (item: OrcamentoItem): string[] => {
    const errors: string[] = [];
    const produto = item.produto;

    if (!produto) return errors;

    // Validar custo unitário
    if (produto.minimo_custo && item.custo_unitario < produto.minimo_custo) {
      errors.push(`Custo unitário abaixo do mínimo (${produto.minimo_custo})`);
    }
    if (produto.maximo_custo && item.custo_unitario > produto.maximo_custo) {
      errors.push(`Custo unitário acima do máximo (${produto.maximo_custo})`);
    }

    // Validar fee
    if (produto.minimo_fee && item.fee_percent < produto.minimo_fee) {
      errors.push(`Fee abaixo do mínimo (${produto.minimo_fee}%)`);
    }
    if (produto.maximo_fee && item.fee_percent > produto.maximo_fee) {
      errors.push(`Fee acima do máximo (${produto.maximo_fee}%)`);
    }

    return errors;
  };

  const saveItems = async () => {
    setSaving(true);

    try {
      const user = await supabase.auth.getUser();
      const userId = user.data.user?.id;

      // Validar todos os itens
      const hasErrors = items.some(item => {
        if (!item.id_produto) return true;
        const errors = validateItem(item);
        return errors.length > 0;
      });

      if (hasErrors) {
        toast({
          title: "Erro de Validação",
          description: "Corrija os erros nos itens antes de salvar",
          variant: "destructive",
        });
        return;
      }

      // Verificar teto do projeto
      const somaValor = calculateTotals().somaValor;
      if (valorMaximoProjeto && somaValor > valorMaximoProjeto) {
        toast({
          title: "Erro",
          description: "Total do orçamento excede o valor máximo do projeto",
          variant: "destructive",
        });
        return;
      }

      // Salvar/atualizar itens
      for (const item of items) {
        if (!item.id_produto) continue;

        const payload = {
          id_orcamento: orcamentoId,
          id_produto: item.id_produto,
          quantidade: item.quantidade,
          custo_unitario: item.custo_unitario,
          valor_unitario: item.valor_unitario,
          fee_percent: item.fee_percent,
          observacao: item.observacao,
          updated_by: userId,
          created_by: !item.id ? userId : undefined,
        };

        if (item.id) {
          const { error } = await supabase
            .from("orcamento_itens")
            .update(payload)
            .eq("id", item.id);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("orcamento_itens")
            .insert({
              orcamento_id: payload.id_orcamento,
              produto_id: payload.id_produto,
              quantidade: payload.quantidade,
              preco_unitario: payload.valor_unitario,
              valor_total: payload.quantidade * payload.valor_unitario,
              descricao: 'Produto',
              observacoes: payload.observacao,
              created_by: payload.created_by,
              updated_by: payload.updated_by
            });

          if (error) throw error;
        }
      }

      toast({
        title: "Sucesso",
        description: "Itens salvos com sucesso",
      });

      // Recarregar itens
      loadItems();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar itens",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const calculateTotals = useCallback(() => {
    const validItems = items.filter(item => item.id_produto && item.quantidade > 0);
    
    const somaCusto = validItems.reduce((sum, item) => sum + (item.quantidade * item.custo_unitario), 0);
    const somaValor = validItems.reduce((sum, item) => sum + (item.quantidade * item.valor_unitario), 0);
    
    // Média ponderada de margem
    const mediaFee = somaCusto > 0 ? 
      validItems.reduce((sum, item) => sum + ((item.quantidade * item.custo_unitario) * item.fee_percent), 0) / somaCusto 
      : 0;
    
    const quantidadeItens = validItems.length;

    return { somaCusto, somaValor, mediaFee, quantidadeItens };
  }, [items]);

  const totals = calculateTotals();
  const exceedsProjectLimit = valorMaximoProjeto && totals.somaValor > valorMaximoProjeto;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo do Orçamento */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo do Orçamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Soma de Custo</label>
              <p className="text-lg font-semibold">
                R$ {totals.somaCusto.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Soma de Valor</label>
              <p className={`text-lg font-semibold ${exceedsProjectLimit ? "text-red-600" : ""}`}>
                R$ {totals.somaValor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Média de Margem</label>
              <p className="text-lg font-semibold">{totals.mediaFee.toFixed(2)}%</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Quantidade de Itens</label>
              <p className="text-lg font-semibold">{totals.quantidadeItens}</p>
            </div>
          </div>

          {valorMaximoProjeto && (
            <div className="mt-4 p-2 bg-muted rounded">
              <p className="text-sm text-muted-foreground">
                Valor máximo do projeto: R$ {valorMaximoProjeto.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}

          {exceedsProjectLimit && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Total do orçamento excede o valor máximo do projeto
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Grid de Produtos */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Produtos</CardTitle>
            {!readonly && (
              <div className="flex gap-2">
                <Button onClick={addNewItem} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Item
                </Button>
                <Button onClick={saveItems} disabled={saving} variant="default">
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {readonly ? "Nenhum produto adicionado" : "Clique em 'Adicionar Item' para começar"}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Row */}
              <div className="grid grid-cols-12 gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
                <div className="col-span-2">Categorias</div>
                <div className="col-span-2">Produto</div>
                <div className="col-span-1">Qtd.</div>
                <div className="col-span-1">Custo Unit.</div>
                <div className="col-span-1">Fee %</div>
                <div className="col-span-1">Valor Unit.</div>
                <div className="col-span-1">Custo Total</div>
                <div className="col-span-1">Valor Total</div>
                <div className="col-span-1">Observação</div>
                {!readonly && <div className="col-span-1">Ações</div>}
              </div>
              
              {items.map((item, index) => {
                const errors = validateItem(item);
                const hasErrors = errors.length > 0;

                return (
                  <div key={index} className={`p-4 border rounded-lg ${hasErrors ? "border-red-200 bg-red-50" : ""}`}>
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Categorias */}
                      <div className="col-span-2 text-xs text-muted-foreground">
                        {item.produto?.categorias_n3?.categorias_n2?.categorias_n1?.nome && (
                          <div>{item.produto.categorias_n3.categorias_n2.categorias_n1.nome}</div>
                        )}
                        {item.produto?.categorias_n3?.categorias_n2?.nome && (
                          <div>{item.produto.categorias_n3.categorias_n2.nome}</div>
                        )}
                        {item.produto?.categorias_n3?.nome && (
                          <div>{item.produto.categorias_n3.nome}</div>
                        )}
                      </div>

                       {/* Produto */}
                      <div className="col-span-2">
                        <ProductSelector
                          value={item.id_produto}
                          onValueChange={(value) => updateItem(index, "id_produto", value)}
                          produtos={produtos}
                          disabled={readonly}
                        />
                      </div>

                      {/* Quantidade */}
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={item.quantidade}
                          onChange={(e) => updateItem(index, "quantidade", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          disabled={readonly}
                        />
                      </div>

                      {/* Custo Unitário */}
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={item.custo_unitario}
                          onChange={(e) => updateItem(index, "custo_unitario", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          disabled={readonly}
                        />
                      </div>

                      {/* Fee Percent */}
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={item.fee_percent}
                          onChange={(e) => updateItem(index, "fee_percent", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.1"
                          disabled={readonly}
                        />
                      </div>

                      {/* Valor Unitário */}
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={item.valor_unitario}
                          onChange={(e) => updateItem(index, "valor_unitario", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          disabled={readonly}
                        />
                      </div>

                      {/* Custo Total */}
                      <div className="col-span-1 text-sm font-medium">
                        R$ {(item.quantidade * item.custo_unitario).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>

                      {/* Valor Total */}
                      <div className="col-span-1 text-sm font-medium">
                        R$ {(item.quantidade * item.valor_unitario).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>

                      {/* Observação */}
                      <div className="col-span-1">
                        <Input
                          value={item.observacao}
                          onChange={(e) => updateItem(index, "observacao", e.target.value)}
                          placeholder="Obs"
                          disabled={readonly}
                        />
                      </div>

                      {/* Ações */}
                      {!readonly && (
                        <div className="col-span-1 flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicateItem(index)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {hasErrors && (
                      <div className="mt-2 text-xs text-red-600">
                        {errors.map((error, i) => (
                          <div key={i}>• {error}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}